import { useEffect, useMemo, useState } from "react";
import { Plane, Filter, RefreshCw } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

const AIRPORTS = [
  { code: "ALL", label: "Todos los aeropuertos" },
  { code: "LPB", label: "La Paz (El Alto · LPB)" },
  { code: "VVI", label: "Santa Cruz (Viru Viru · VVI)" },
  { code: "CBB", label: "Cochabamba (Wilstermann · CBB)" },
];

const TYPES = [
  { code: "all", label: "Llegadas y salidas" },
  { code: "arrival", label: "Solo llegadas" },
  { code: "departure", label: "Solo salidas" },
];

const STATUS_FILTERS = [
  { code: "all", label: "Todos los estados" },
  { code: "alert", label: "Solo demorados y cancelados" },
];

export default function FlightsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [airport, setAirport] = useState("ALL");
  const [tipo, setTipo] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [refreshing, setRefreshing] = useState(false);

  const load = () => {
    setRefreshing(true);
    const params = { limit: 500 };
    if (airport !== "ALL") params.airport = airport;
    if (tipo !== "all") params.tipo = tipo;
    api.get("/flights/all", { params })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => { setLoading(false); setRefreshing(false); });
  };
  useEffect(load, [airport, tipo]); // eslint-disable-line

  const visible = useMemo(() => {
    if (statusF === "alert") return items.filter((f) => f.is_alert);
    return items;
  }, [items, statusF]);

  return (
    <>
      <Header />
      <main className="max-w-[1440px] mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="flights-page">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "var(--accent)" }}>
            <Plane size={20} color="#fff" />
          </div>
          <div>
            <p className="overline accent-text">VUELOS · BOLIVIA</p>
            <h1 className="font-headline uppercase text-3xl md:text-5xl">Estado de vuelos en vivo</h1>
          </div>
        </div>
        <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>
          Llegadas y salidas en tiempo real desde los principales aeropuertos de Bolivia. Datos oficiales de <a className="accent-text font-bold" href="https://fids.naabol.gob.bo/" target="_blank" rel="noopener noreferrer">NAABOL</a>.
        </p>

        <div className="flex flex-wrap gap-3 mb-6 items-center" data-testid="flights-filters">
          <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
            <Filter size={14} /> FILTRAR
          </div>
          <select value={airport} onChange={(e) => setAirport(e.target.value)} className="px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="filter-airport">
            {AIRPORTS.map((a) => <option key={a.code} value={a.code}>{a.label}</option>)}
          </select>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="filter-tipo">
            {TYPES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="filter-status">
            {STATUS_FILTERS.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
          </select>
          <button onClick={load} disabled={refreshing} className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 border rounded-sm text-sm font-bold" style={{ borderColor: "var(--border)" }} data-testid="refresh-btn">
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Actualizar
          </button>
        </div>

        <div className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
          {visible.length} vuelos · {visible.filter(f => f.is_alert).length} en alerta
        </div>

        {loading ? (
          <div className="space-y-2">
            {Array(8).fill(0).map((_, i) => <div key={i} className="h-12 bg-[color:var(--surface-2)] rounded-sm animate-pulse" />)}
          </div>
        ) : (
          <div className="overflow-x-auto border rounded-sm" style={{ borderColor: "var(--border)" }}>
            <table className="w-full text-sm" data-testid="flights-table">
              <thead style={{ background: "var(--surface-2)" }}>
                <tr>
                  <th className="text-left p-3 font-bold text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Aerolínea</th>
                  <th className="text-left p-3 font-bold text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Vuelo</th>
                  <th className="text-left p-3 font-bold text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Ruta</th>
                  <th className="text-left p-3 font-bold text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Tipo</th>
                  <th className="text-center p-3 font-bold text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Estim.</th>
                  <th className="text-center p-3 font-bold text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Real</th>
                  <th className="text-center p-3 font-bold text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Puerta</th>
                  <th className="text-center p-3 font-bold text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((f) => (
                  <tr key={f.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`flight-row-${f.id}`}>
                    <td className="p-3"><span className="font-bold">{f.airline_short || f.airline}</span></td>
                    <td className="p-3 font-mono">{f.flight_code}</td>
                    <td className="p-3">
                      <span className="font-bold">{f.from_iata}</span>
                      <span className="mx-1 opacity-50">→</span>
                      <span className="font-bold">{f.to_iata}</span>
                      <p className="text-xs opacity-60">{f.from_name} → {f.to_name}</p>
                    </td>
                    <td className="p-3 text-xs uppercase tracking-wider">{f.tipo === "arrival" ? "Llegada" : "Salida"}</td>
                    <td className="p-3 text-center font-mono">{f.scheduled || "—"}</td>
                    <td className="p-3 text-center font-mono">{f.real || "—"}</td>
                    <td className="p-3 text-center">{f.gate || "—"}</td>
                    <td className="p-3 text-center">
                      <span className={`fl-pill fl-pill-${f.status_class} ${f.status_class === "cancelled" ? "fl-pulse" : ""}`}>{f.status}</span>
                    </td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr><td colSpan={8} className="p-8 text-center" style={{ color: "var(--text-muted)" }}>No hay vuelos con esos filtros.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs mt-6" style={{ color: "var(--text-muted)" }}>
          Fuente: <a href="https://fids.naabol.gob.bo/" target="_blank" rel="noopener noreferrer" className="accent-text font-bold">NAABOL FIDS</a> · datos actualizados cada 5 minutos.
        </p>
      </main>
      <Footer />
    </>
  );
}
